// Reusable Gantt-style timeline: one row per item, a bar spanning
// item.start → item.end (years), coloured by party. Used by the
// "X over time" Explore-stats pages (women in Parliament, changed
// electorate/party, First Nations, party timeline).

import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { partyColor } from './charts';

export default function GanttTimeline({ items, rowHeight = 16, maxHeight = 560 }) {
  const { minYear, maxYear, span } = useMemo(() => {
    if (!items || !items.length) return { minYear: 0, maxYear: 0, span: 1 };
    const mn = Math.min(...items.map((i) => i.start));
    const mx = Math.max(...items.map((i) => i.end));
    return { minYear: mn, maxYear: mx, span: Math.max(mx - mn, 1) };
  }, [items]);

  if (!items || !items.length) {
    return <Typography color="text.secondary">No data.</Typography>;
  }

  // Decade gridline labels across the axis.
  const ticks = [];
  for (let y = Math.ceil(minYear / 10) * 10; y <= maxYear; y += 10) ticks.push(y);

  return (
    <Box>
      {/* Year axis */}
      <Box sx={{ position: 'relative', height: 18, ml: '180px', mb: 0.5 }}>
        {ticks.map((y) => (
          <Box key={y} sx={{
            position: 'absolute', left: `${((y - minYear) / span) * 100}%`,
            fontSize: 11, color: 'text.secondary', transform: 'translateX(-50%)',
          }}>{y}</Box>
        ))}
      </Box>
      <Box sx={{ maxHeight, overflowY: 'auto', pr: 1 }}>
        {items.map((it, idx) => (
          <Box key={`${it.label}-${idx}`}
               sx={{ display: 'flex', alignItems: 'center', height: rowHeight }}>
            <Box sx={{
              width: 180, flexShrink: 0, fontSize: 11, pr: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }} title={it.label}>{it.label}</Box>
            <Box sx={{ position: 'relative', flex: 1, height: '100%' }}>
              <Tooltip title={`${it.label} — ${it.party || ''} (${it.start}–${
                it.current ? 'present' : it.end})`}>
                <Box sx={{
                  position: 'absolute',
                  left: `${((it.start - minYear) / span) * 100}%`,
                  width: `${Math.max(((it.end - it.start) / span) * 100, 0.4)}%`,
                  top: '20%', height: '60%', borderRadius: '2px',
                  backgroundColor: partyColor(it.party),
                }} />
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
